import React from 'react';
import { Shield, Globe, FileCheck, History, ArrowRight, Lock, Building2, Users, Landmark, DollarSign, TrendingUp, Zap, CheckCircle2, BarChart3 } from 'lucide-react';
interface LandingPageProps {
  onLaunchDemo: () => void;
}
export function LandingPage({
  onLaunchDemo
}: LandingPageProps) {
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-477">
      {/* Header */}
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50" data-id="element-478">
        <div className="flex items-center gap-3" data-id="element-479">
          <img src="https://cdn.magicpatterns.com/uploads/dN76LW7ZJU33sweBnWUYDh/Datmont_with_Apostel_Seal_copy.png" alt="Datmont Logo" className="h-10 object-contain" data-id="element-480" />
          <span className="text-xl font-bold text-datmont-900 tracking-tight" data-id="element-481">
            Datmont™
          </span>
        </div>
        <div className="hidden md:flex items-center gap-6" data-id="element-482">
          <button className="text-sm font-medium text-slate-600 hover:text-datmont-900 transition-colors" data-id="element-483">
            Institutional Login
          </button>
          <button onClick={onLaunchDemo} className="px-5 py-2.5 bg-datmont-900 text-white text-sm font-medium rounded-lg hover:bg-datmont-800 transition-all shadow-sm hover:shadow-md" data-id="element-484">
            Launch Demo
          </button>
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1" data-id="element-485">
        <section className="relative px-6 py-20 md:py-32 overflow-hidden bg-slate-50" data-id="element-486">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-50" data-id="element-487"></div>

          <div className="max-w-5xl mx-auto text-center relative z-10" data-id="element-488">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-datmont-blue text-sm font-semibold mb-8 border border-blue-100 shadow-sm" data-id="element-489">
              <Shield className="w-4 h-4" data-id="element-490" />
              <span data-id="element-491">Enterprise-Grade Security</span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-datmont-900 tracking-tight mb-6 leading-tight" data-id="element-492">
              Advanced Global Document <br className="hidden md:block" data-id="element-493" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-datmont-900 via-datmont-800 to-datmont-blue" data-id="element-494">
                Authentication Platform
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-495">
              Datmont is a three-party document authentication infrastructure
              connecting issuers, users, and receivers. We function as a global
              trust relay layer that verifies document origin, integrity, and
              authenticity in real time.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-496">
              <button onClick={onLaunchDemo} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-datmont-blue text-white text-lg font-medium rounded-xl hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:-translate-y-0.5" data-id="element-497">
                Explore Platform Demo
                <ArrowRight className="w-5 h-5" data-id="element-498" />
              </button>
              <button onClick={() => alert('Institutional Access Request Form would open here.')} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-white text-datmont-900 text-lg font-medium rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all shadow-sm" data-id="element-499">
                <Lock className="w-5 h-5 text-slate-400" data-id="element-500" />
                Request Institutional Access
              </button>
            </div>
          </div>
        </section>

        {/* Stats / Trust */}
        <section className="py-12 border-y border-slate-100 bg-white" data-id="element-501">
          <div className="max-w-7xl mx-auto px-6" data-id="element-502">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest text-center mb-8" data-id="element-503">
              Trusted by leading institutions worldwide
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 lg:gap-24 opacity-60 grayscale" data-id="element-504">
              <div className="flex items-center gap-2" data-id="element-505">
                <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-white font-serif font-bold text-xs" data-id="element-506">
                  FBI
                </div>
                <span className="text-xl font-bold text-slate-800" data-id="element-507">FBI</span>
              </div>
              <div className="text-xl font-bold text-slate-800 flex items-center gap-2" data-id="element-508">
                <Shield className="w-6 h-6" data-id="element-509" /> Home Office UK
              </div>
              <div className="text-xl font-bold text-slate-800 flex items-center gap-2" data-id="element-510">
                <Globe className="w-6 h-6" data-id="element-511" /> Ministry of Interior UAE
              </div>
              <div className="text-xl font-bold text-slate-800" data-id="element-512">VFS Global</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 max-w-4xl mx-auto text-center" data-id="element-513">
              <div data-id="element-514">
                <div className="text-4xl font-extrabold text-datmont-900 mb-2" data-id="element-515">
                  2.4M+
                </div>
                <div className="text-sm font-medium text-slate-500" data-id="element-516">
                  Verifications Today
                </div>
              </div>
              <div data-id="element-517">
                <div className="text-4xl font-extrabold text-datmont-900 mb-2" data-id="element-518">
                  120+
                </div>
                <div className="text-sm font-medium text-slate-500" data-id="element-519">
                  Issuers Connected
                </div>
              </div>
              <div data-id="element-520">
                <div className="text-4xl font-extrabold text-datmont-900 mb-2" data-id="element-521">
                  9
                </div>
                <div className="text-sm font-medium text-slate-500" data-id="element-522">
                  Core Workflows
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="py-24 bg-slate-50" data-id="element-523">
          <div className="max-w-7xl mx-auto px-6" data-id="element-524">
            <div className="text-center mb-16" data-id="element-525">
              <h2 className="text-3xl md:text-4xl font-bold text-datmont-900 mb-4" data-id="element-526">
                Core Capabilities
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-527">
                A comprehensive suite of tools designed for seamless, secure,
                and verifiable document handling across international borders.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-528">
              {[{
              icon: <Globe className="w-6 h-6 text-datmont-blue" data-id="element-529" />,
              title: 'Global Document Verification',
              desc: 'Instant authentication across borders with real-time issuer verification.'
            }, {
              icon: <Shield className="w-6 h-6 text-datmont-blue" data-id="element-530" />,
              title: 'Digital Apostille',
              desc: 'Cryptographically signed, tamper-proof seals that guarantee document integrity.'
            }, {
              icon: <FileCheck className="w-6 h-6 text-datmont-blue" data-id="element-531" />,
              title: 'Institutional Trust Network',
              desc: 'Connect directly with 120+ verified issuers and processing centers globally.'
            }, {
              icon: <History className="w-6 h-6 text-datmont-blue" data-id="element-532" />,
              title: 'Audit Trail & Compliance',
              desc: 'Complete, immutable verification history for strict regulatory compliance.'
            }].map((feature, i) => <div key={i} className="p-8 rounded-2xl bg-white border border-slate-100 hover:border-blue-100 hover:shadow-lg hover:shadow-blue-900/5 transition-all group" data-id="element-533">
                  <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-534">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-datmont-900 mb-3" data-id="element-535">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-536">
                    {feature.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* How It Works (Three-Party Infrastructure) */}
        <section className="py-24 bg-white border-t border-slate-100" data-id="element-537">
          <div className="max-w-7xl mx-auto px-6" data-id="element-538">
            <div className="text-center mb-16" data-id="element-539">
              <h2 className="text-3xl md:text-4xl font-bold text-datmont-900 mb-4" data-id="element-540">
                The Three-Party Infrastructure
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto" data-id="element-541">
                Datmont functions as a trust relay layer that verifies document
                origin, integrity, and authenticity in real time.
              </p>
            </div>

            {/* Flow Diagram */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-4 lg:gap-8 mb-16" data-id="element-542">
              {/* Issuer */}
              <div className="flex-1 w-full max-w-sm bg-slate-50 rounded-2xl p-8 border border-slate-200 text-center relative" data-id="element-543">
                <div className="w-16 h-16 mx-auto bg-blue-100 text-datmont-blue rounded-full flex items-center justify-center mb-6" data-id="element-544">
                  <Building2 className="w-8 h-8" data-id="element-545" />
                </div>
                <h3 className="text-xl font-bold text-datmont-900 mb-3" data-id="element-546">
                  1. Issuer
                </h3>
                <p className="text-sm text-slate-600 mb-4" data-id="element-547">
                  Government agencies (FBI, State Departments, Vital Records
                  Offices) digitally seal documents through a Datmont issuer
                  node.
                </p>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center text-slate-300" data-id="element-548">
                <ArrowRight className="w-8 h-8" data-id="element-549" />
              </div>

              {/* Datmont Trust Layer */}
              <div className="flex-1 w-full max-w-sm bg-datmont-900 rounded-2xl p-8 border border-datmont-800 text-center relative shadow-xl transform md:-translate-y-4" data-id="element-550">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-datmont-blue text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider" data-id="element-551">
                  Trust Layer
                </div>
                <div className="w-16 h-16 mx-auto bg-white/10 text-white rounded-full flex items-center justify-center mb-6 mt-2" data-id="element-552">
                  <Shield className="w-8 h-8" data-id="element-553" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-554">
                  2. User & Datmont
                </h3>
                <p className="text-sm text-slate-300 mb-4" data-id="element-555">
                  The user receives the document embedded with a verification
                  signature. Datmont acts as the immutable relay.
                </p>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center text-slate-300" data-id="element-556">
                <ArrowRight className="w-8 h-8" data-id="element-557" />
              </div>

              {/* Receiver */}
              <div className="flex-1 w-full max-w-sm bg-slate-50 rounded-2xl p-8 border border-slate-200 text-center relative" data-id="element-558">
                <div className="w-16 h-16 mx-auto bg-green-100 text-datmont-green rounded-full flex items-center justify-center mb-6" data-id="element-559">
                  <Landmark className="w-8 h-8" data-id="element-560" />
                </div>
                <h3 className="text-xl font-bold text-datmont-900 mb-3" data-id="element-561">
                  3. Receiver
                </h3>
                <p className="text-sm text-slate-600 mb-4" data-id="element-562">
                  Visa centers, embassies, banks, or employers submit the
                  document to the Datmont API for instant verification.
                </p>
              </div>
            </div>

            {/* Callout */}
            <div className="max-w-4xl mx-auto bg-blue-50 border border-blue-100 rounded-xl p-6 flex items-start gap-4" data-id="element-563">
              <Zap className="w-6 h-6 text-datmont-blue shrink-0 mt-1" data-id="element-564" />
              <div data-id="element-565">
                <h4 className="text-base font-bold text-datmont-900 mb-1" data-id="element-566">
                  Not just another e-signature tool.
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-567">
                  Datmont is not an e-signature tool or document storage
                  platform. It is a global verification infrastructure —
                  comparable in positioning to{' '}
                  <span className="font-semibold" data-id="element-568">SWIFT for banking</span> or{' '}
                  <span className="font-semibold" data-id="element-569">Visa for payments</span>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Market Demand */}
        <section className="py-24 bg-datmont-900 text-white" data-id="element-570">
          <div className="max-w-7xl mx-auto px-6" data-id="element-571">
            <div className="text-center mb-16" data-id="element-572">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-573">
                Global Market Demand
              </h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-574">
                The scale of document authentication events represents a massive
                infrastructure opportunity.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-575">
              {/* US Market */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm hover:bg-white/10 transition-colors" data-id="element-576">
                <div className="flex items-center gap-3 mb-6" data-id="element-577">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center" data-id="element-578">
                    <BarChart3 className="w-5 h-5 text-blue-400" data-id="element-579" />
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-580">United States</h3>
                </div>
                <ul className="space-y-4 mb-8" data-id="element-581">
                  <li className="flex items-start gap-3 text-sm text-slate-300" data-id="element-582">
                    <CheckCircle2 className="w-5 h-5 text-datmont-blue shrink-0" data-id="element-583" />
                    <span data-id="element-584">
                      <strong className="text-white" data-id="element-585">~5M</strong> FBI background
                      checks/year
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-300" data-id="element-586">
                    <CheckCircle2 className="w-5 h-5 text-datmont-blue shrink-0" data-id="element-587" />
                    <span data-id="element-588">
                      <strong className="text-white" data-id="element-589">~10M</strong> Birth
                      certificates/year (inc. reissues)
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-300" data-id="element-590">
                    <CheckCircle2 className="w-5 h-5 text-datmont-blue shrink-0" data-id="element-591" />
                    <span data-id="element-592">
                      <strong className="text-white" data-id="element-593">~5M</strong> Marriage
                      certificates/year
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-300" data-id="element-594">
                    <CheckCircle2 className="w-5 h-5 text-datmont-blue shrink-0" data-id="element-595" />
                    <span data-id="element-596">
                      <strong className="text-white" data-id="element-597">~1-2M</strong> Apostille &
                      authentication/year
                    </span>
                  </li>
                </ul>
                <div className="pt-6 border-t border-white/10" data-id="element-598">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1" data-id="element-599">
                    Total Estimated Volume
                  </p>
                  <p className="text-3xl font-extrabold text-white" data-id="element-600">
                    20-30M
                    <span className="text-lg text-slate-400 font-normal ml-2" data-id="element-601">
                      events/yr
                    </span>
                  </p>
                </div>
              </div>

              {/* UK Market */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm hover:bg-white/10 transition-colors" data-id="element-602">
                <div className="flex items-center gap-3 mb-6" data-id="element-603">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center" data-id="element-604">
                    <BarChart3 className="w-5 h-5 text-blue-400" data-id="element-605" />
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-606">United Kingdom</h3>
                </div>
                <ul className="space-y-4 mb-8" data-id="element-607">
                  <li className="flex items-start gap-3 text-sm text-slate-300" data-id="element-608">
                    <CheckCircle2 className="w-5 h-5 text-datmont-blue shrink-0" data-id="element-609" />
                    <span data-id="element-610">
                      <strong className="text-white" data-id="element-611">~6M</strong> Criminal
                      record checks/year
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-300" data-id="element-612">
                    <CheckCircle2 className="w-5 h-5 text-datmont-blue shrink-0" data-id="element-613" />
                    <span data-id="element-614">
                      <strong className="text-white" data-id="element-615">~6M</strong> Birth
                      certificates/year
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-300" data-id="element-616">
                    <CheckCircle2 className="w-5 h-5 text-datmont-blue shrink-0" data-id="element-617" />
                    <span data-id="element-618">
                      <strong className="text-white" data-id="element-619">~1M</strong> Apostille
                      services/year
                    </span>
                  </li>
                </ul>
                <div className="pt-6 border-t border-white/10 mt-auto" data-id="element-620">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1" data-id="element-621">
                    Total Estimated Volume
                  </p>
                  <p className="text-3xl font-extrabold text-white" data-id="element-622">
                    12-15M
                    <span className="text-lg text-slate-400 font-normal ml-2" data-id="element-623">
                      events/yr
                    </span>
                  </p>
                </div>
              </div>

              {/* Global Market */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm hover:bg-white/10 transition-colors" data-id="element-624">
                <div className="flex items-center gap-3 mb-6" data-id="element-625">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center" data-id="element-626">
                    <Globe className="w-5 h-5 text-blue-400" data-id="element-627" />
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-628">Global (Visa Flow)</h3>
                </div>
                <ul className="space-y-4 mb-8" data-id="element-629">
                  <li className="flex items-start gap-3 text-sm text-slate-300" data-id="element-630">
                    <CheckCircle2 className="w-5 h-5 text-datmont-blue shrink-0" data-id="element-631" />
                    <span data-id="element-632">
                      <strong className="text-white" data-id="element-633">80-100M</strong> Visa
                      applications/year
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-300" data-id="element-634">
                    <CheckCircle2 className="w-5 h-5 text-datmont-blue shrink-0" data-id="element-635" />
                    <span data-id="element-636">
                      <strong className="text-white" data-id="element-637">2-5</strong> Supporting
                      documents required per application
                    </span>
                  </li>
                </ul>
                <div className="pt-6 border-t border-white/10 mt-auto" data-id="element-638">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1" data-id="element-639">
                    Total Estimated Volume
                  </p>
                  <p className="text-3xl font-extrabold text-white" data-id="element-640">
                    200-400M
                    <span className="text-lg text-slate-400 font-normal ml-2" data-id="element-641">
                      docs/yr
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Revenue Potential & Market Opportunity */}
        <section className="py-24 bg-slate-50" data-id="element-642">
          <div className="max-w-7xl mx-auto px-6" data-id="element-643">
            <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-644">
              <div data-id="element-645">
                <h2 className="text-3xl md:text-4xl font-bold text-datmont-900 mb-6" data-id="element-646">
                  Revenue Potential & Market Opportunity
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed" data-id="element-647">
                  Current systems rely on manual verification, embassy
                  authentication, and outdated apostille frameworks. Fraud risks
                  continue to increase while processing delays remain
                  significant.
                </p>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-648">
                  Datmont replaces paper-based authentication with real-time
                  digital trust through programmable digital apostille using
                  cryptographic verification and issuer validation nodes.
                </p>

                <div className="space-y-6" data-id="element-649">
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-6" data-id="element-650">
                    <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center shrink-0" data-id="element-651">
                      <DollarSign className="w-7 h-7 text-datmont-green" data-id="element-652" />
                    </div>
                    <div data-id="element-653">
                      <p className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-654">
                        US Market Potential
                      </p>
                      <p className="text-2xl font-extrabold text-datmont-900" data-id="element-655">
                        $25M - $100M{' '}
                        <span className="text-base font-normal text-slate-500" data-id="element-656">
                          annually
                        </span>
                      </p>
                      <p className="text-xs text-slate-500 mt-1" data-id="element-657">
                        Based on $1/verification model
                      </p>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-6" data-id="element-658">
                    <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center shrink-0" data-id="element-659">
                      <TrendingUp className="w-7 h-7 text-datmont-blue" data-id="element-660" />
                    </div>
                    <div data-id="element-661">
                      <p className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-662">
                        Global Opportunity
                      </p>
                      <p className="text-2xl font-extrabold text-datmont-900" data-id="element-663">
                        $300M - $1.5B{' '}
                        <span className="text-base font-normal text-slate-500" data-id="element-664">
                          annually
                        </span>
                      </p>
                      <p className="text-xs text-slate-500 mt-1" data-id="element-665">
                        Excluding enterprise licensing & gov contracts
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative" data-id="element-666">
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-100 to-indigo-50 rounded-3xl transform rotate-3 scale-105" data-id="element-667"></div>
                <div className="relative bg-white p-8 rounded-3xl shadow-xl border border-slate-100" data-id="element-668">
                  <h3 className="text-xl font-bold text-datmont-900 mb-6 border-b border-slate-100 pb-4" data-id="element-669">
                    The Datmont Advantage
                  </h3>
                  <ul className="space-y-6" data-id="element-670">
                    <li className="flex gap-4" data-id="element-671">
                      <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center shrink-0 mt-1" data-id="element-672">
                        <span className="text-red-500 font-bold" data-id="element-673">✕</span>
                      </div>
                      <div data-id="element-674">
                        <p className="font-bold text-slate-900" data-id="element-675">
                          Legacy Systems
                        </p>
                        <p className="text-sm text-slate-600 mt-1" data-id="element-676">
                          Manual processing, paper apostilles, high fraud risk,
                          weeks of delays, expensive courier fees.
                        </p>
                      </div>
                    </li>
                    <li className="flex gap-4" data-id="element-677">
                      <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center shrink-0 mt-1" data-id="element-678">
                        <CheckCircle2 className="w-5 h-5 text-datmont-green" data-id="element-679" />
                      </div>
                      <div data-id="element-680">
                        <p className="font-bold text-slate-900" data-id="element-681">
                          Datmont Infrastructure
                        </p>
                        <p className="text-sm text-slate-600 mt-1" data-id="element-682">
                          Instant API verification, cryptographic certainty,
                          zero transit time, scalable digital trust.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Strategic Positioning */}
        <section className="py-32 bg-datmont-900 relative overflow-hidden" data-id="element-683">
          {/* Background effects */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-datmont-800 via-datmont-900 to-datmont-900 opacity-50" data-id="element-684"></div>

          <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-685">
            <Shield className="w-16 h-16 text-datmont-blue mx-auto mb-8 opacity-80" data-id="element-686" />
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-8 tracking-tight" data-id="element-687">
              The Trust Layer for the Modern World
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-688">
              Datmont is positioned as the global document authentication
              infrastructure — the trust layer between governments, individuals,
              and institutions. It enables instant verification, reduces fraud,
              and eliminates delays associated with traditional document
              legalization.
            </p>
            <p className="text-lg text-slate-400 mb-12 max-w-3xl mx-auto" data-id="element-689">
              A scalable, monetizable, and globally relevant infrastructure
              solution aligned with modern digital identity and compliance
              needs. Datmont has the potential to become a foundational layer
              for document verification across governments, financial
              institutions, and international systems.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-690">
              <button onClick={onLaunchDemo} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-datmont-blue text-white text-lg font-medium rounded-xl hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:-translate-y-0.5" data-id="element-691">
                Explore Platform Demo
                <ArrowRight className="w-5 h-5" data-id="element-692" />
              </button>
              <button onClick={() => alert('Institutional Access Request Form would open here.')} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white text-lg font-medium rounded-xl border border-white/20 hover:bg-white/20 transition-all" data-id="element-693">
                <Lock className="w-5 h-5 text-white/70" data-id="element-694" />
                Request Institutional Access
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-datmont-900 text-slate-400 py-12 border-t border-datmont-800" data-id="element-695">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-696">
          <div className="flex items-center gap-3" data-id="element-697">
            <img src="https://cdn.magicpatterns.com/uploads/dN76LW7ZJU33sweBnWUYDh/Datmont_with_Apostel_Seal_copy.png" alt="Datmont Logo" className="h-8 object-contain brightness-0 invert opacity-80" data-id="element-698" />
            <span className="text-lg font-bold text-white tracking-tight" data-id="element-699">
              Datmont™
            </span>
          </div>
          <p className="text-sm" data-id="element-700">
            © 2026 Datmont™ — Global Document Authentication. All rights
            reserved.
          </p>
          <div className="flex gap-6 text-sm font-medium" data-id="element-701">
            <a href="#" className="hover:text-white transition-colors" data-id="element-702">
              Privacy
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-703">
              Terms
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-704">
              Security
            </a>
          </div>
        </div>
      </footer>
    </div>;
}